import {
  Cancel,
  Done,
  NotInterested,
  Pending,
  Reviews,
  Functions,
  Work,
} from "@mui/icons-material";
import CryptoJS from "crypto-js";
import { localize } from "./localize";
import NoDataLabel from "../components/common/NoDataLabel";
import {
  AdditionalStatusCodeEnum,
  getAdditionalCostStatusName,
} from "./EntitiesManagement";

export const getFileMaxSize = () => 3 * 1024 * 1024; // 3MB

export const REPORT_TITLE_DEFAULT_BG = "#10366e";
export const REPORT_TITLE_DEFAULT_FG = "#ffffff";

// Applies the report section title colors as CSS variables on the document root
// so every element using `.report_title_section` (all report/print section titles)
// inherits the company-configured background and foreground colors.
export const applyReportTitleColors = (backgroundHex, foregroundHex) => {
  if (typeof document === "undefined" || !document.documentElement) {
    return;
  }
  document.documentElement.style.setProperty(
    "--report-title-bg",
    backgroundHex || REPORT_TITLE_DEFAULT_BG,
  );
  document.documentElement.style.setProperty(
    "--report-title-fg",
    foregroundHex || REPORT_TITLE_DEFAULT_FG,
  );
};

export const encryptForUrl = (textToEncrypt) =>
  encodeURIComponent(CryptoJS.AES.encrypt(textToEncrypt, localize.app_name));

export const decryptForUrl = (textToDecrypt) =>
  CryptoJS.AES.decrypt(
    decodeURIComponent(textToDecrypt),
    localize.app_name,
  ).toString(CryptoJS.enc.Utf8);

export const getPaymentTypeModeLabel = (paymentMode) => {
  switch (paymentMode) {
    case "PERCENTAGE":
      return localize.percentage;
    case "FIXED":
      return localize.fixed_value;
    default:
      return null;
  }
};

export const getFieldTypeName = (code) => {
  switch (code) {
    case "STRING":
      return localize.field_type_string;
    case "INT":
      return localize.field_type_int;
    case "DOUBLE":
      return localize.field_type_double;
    case "DATE":
      return localize.field_type_date;
    case "TIME":
      return localize.field_type_time;
    case "DATETIME":
      return localize.field_type_datetime;
    case "SIGNATURE":
      return localize.field_type_signature;
    case "PHOTO":
      return localize.field_type_photo;
    case "BOOLEAN":
      return localize.field_type_boolean;
    case "COMBOBOX":
      return localize.field_type_combobox;
    default:
      return localize.no_data;
  }
};

export const getArrayWithFieldTypes = () => [
  {
    Id: "STRING",
    Name: getFieldTypeName("STRING"),
  },
  {
    Id: "BOOLEAN",
    Name: getFieldTypeName("BOOLEAN"),
  },
  {
    Id: "INT",
    Name: getFieldTypeName("INT"),
  },
  {
    Id: "DOUBLE",
    Name: getFieldTypeName("DOUBLE"),
  },
  {
    Id: "DATE",
    Name: getFieldTypeName("DATE"),
  },
  {
    Id: "TIME",
    Name: getFieldTypeName("TIME"),
  },
  {
    Id: "DATETIME",
    Name: getFieldTypeName("DATETIME"),
  },
  {
    Id: "SIGNATURE",
    Name: getFieldTypeName("SIGNATURE"),
  },
  {
    Id: "PHOTO",
    Name: getFieldTypeName("PHOTO"),
  },
  {
    Id: "COMBOBOX",
    Name: getFieldTypeName("COMBOBOX"),
  },
];

export const getAlertTypeFromOperationalStatusId = (id) => {
  switch (id) {
    case OperationalStatusEnum.PendingReview:
      return "warning";
    case OperationalStatusEnum.Finished:
      return "success";
    case OperationalStatusEnum.NotFinished:
    case OperationalStatusEnum.Canceled:
      return "error";
    default:
      return "info";
  }
};

export const getOperationalStatusNameFromId = (id) => {
  switch (id) {
    case OperationalStatusEnum.TotalServiceOrders:
      return localize.totalServiceOrders;
    case OperationalStatusEnum.Canceled:
      return localize.cancelled;
    case OperationalStatusEnum.ToExecute:
      return localize.to_execute;
    case OperationalStatusEnum.InExecution:
      return localize.in_execution;
    case OperationalStatusEnum.PendingReview:
      return localize.pending_review;
    case OperationalStatusEnum.Finished:
      return localize.finished;
    case OperationalStatusEnum.NotFinished:
      return localize.not_finished;
    default:
      return localize.no_data;
  }
};

export const getOperationalStatusStyleName = (id) => {
  switch (id) {
    case OperationalStatusEnum.Canceled:
      return "default_operational_status cancelled_operational_status";
    case OperationalStatusEnum.InExecution:
      return "default_operational_status in_execution_operational_status";
    case OperationalStatusEnum.PendingReview:
      return "default_operational_status pending_review_operational_status";
    case OperationalStatusEnum.Finished:
      return "default_operational_status finished_operational_status";
    case OperationalStatusEnum.NotFinished:
      return "default_operational_status not_finished_operational_status";
    default:
      return "default_operational_status";
  }
};

export const getOperationalStatusIconById = (id) => {
  switch (id) {
    case OperationalStatusEnum.TotalServiceOrders:
      return <Functions style={{ color: "green" }} />;
    case OperationalStatusEnum.Canceled:
      return <Cancel style={{ color: "#ff0000" }} />;
    case OperationalStatusEnum.InExecution:
      return <Work style={{ color: "#21469C" }} />;
    case OperationalStatusEnum.PendingReview:
      return <Reviews style={{ color: "#E0CD5F" }} />;
    case OperationalStatusEnum.Finished:
      return <Done style={{ color: "#0E601D" }} />;
    case OperationalStatusEnum.NotFinished:
      return <NotInterested style={{ color: "#A11717" }} />;
    default:
      return <Pending style={{ color: "lightgray" }} />;
  }
};

export const getOperationalStatusForeColorById = (id) => {
  switch (id) {
    case OperationalStatusEnum.ToExecute:
    case OperationalStatusEnum.PendingReview:
      return "rgba(0,0,0,0.7)";
    default:
      return "#ffffff";
  }
};

export const getOperationalStatusColorById = (id) => {
  switch (id) {
    case OperationalStatusEnum.TotalServiceOrders:
      return "green";
    case OperationalStatusEnum.Canceled:
      return "#ff0000";
    case OperationalStatusEnum.InExecution:
      return "#21469C";
    case OperationalStatusEnum.PendingReview:
      return "#E0CD5F";
    case OperationalStatusEnum.Finished:
      return "#0E601D";
    case OperationalStatusEnum.NotFinished:
      return "#A11717";
    default:
      return "lightgray";
  }
};

export const ProcessSymbol = () => "=>";

export const PaginationLimit = () => 10;

export const getExpectedSolutionDate = (event) => {
  if (event?.ExpectedSolutionDate) {
    if (!event.ExpectedSolutionFinalDate) {
      return GetDateInMMDDYYYYFormat(new Date(event.ExpectedSolutionDate));
    }

    const initial = GetDateInMMDDYYYYFormat(
      new Date(event.ExpectedSolutionDate),
    );

    const final = GetDateInMMDDYYYYFormat(
      new Date(event.ExpectedSolutionFinalDate),
    );

    if (initial === final) {
      return initial;
    }

    return `${initial} - ${final}`;
  }

  return localize.no_data;
};

export const getExpectedSolutionDateUI = (event) => {
  if (event?.ExpectedSolutionDate) {
    if (!event.ExpectedSolutionFinalDate) {
      return GetDateInMMDDYYYYFormat(new Date(event.ExpectedSolutionDate));
    }

    const initial = GetDateInMMDDYYYYFormat(
      new Date(event.ExpectedSolutionDate),
    );

    const final = GetDateInMMDDYYYYFormat(
      new Date(event.ExpectedSolutionFinalDate),
    );

    if (initial === final) {
      return initial;
    }

    return `${initial} - ${final}`;
  }

  return <NoDataLabel />;
};

export const GetDateInDDMMYYYYFormat = (date) => {
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  return `${da}/${mo}/${ye}`;
};

export const GetDateInMMDDYYYYFormat = (date) => {
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  return `${mo}/${da}/${ye}`;
};

export const GetDateInYYYYMMDDFormatWithSeparator = (date, separator) => {
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  return `${ye}${separator}${mo}${separator}${da}`;
};

export const GetDateInYYYYMMDDWithoutTimeFormat = (date, utc, final) => {
  const ye = utc
    ? date.getUTCFullYear()
    : new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
  const mo = utc
    ? convertToValueWithZero(date.getUTCMonth() + 1)
    : new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
  const da = utc
    ? convertToValueWithZero(date.getUTCDate())
    : new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  return `${ye}-${mo}-${da} ${final ? "23:59:59" : "00:00:00"}`;
};

export const GetDateInYYYYMMDDHHmmss = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  return `${year}-${convertToValueWithZero(month)}-${convertToValueWithZero(
    day,
  )} ${convertToValueWithZero(hours)}:${convertToValueWithZero(
    minutes,
  )}:${convertToValueWithZero(seconds)}`;
};

const convertToValueWithZero = (value) => {
  if (value < 10) {
    return `0${value}`;
  }

  return value;
};

export const getFirstDayOfMonth = (date) => {
  const firstDay = !!date ? date.setDate(1) : new Date().setDate(1);
  return new Date(firstDay);
};

export const getEvidenceTypeExtension = (evidenceTypeId) => {
  const evidenceTypeObj = {
    1: `image/png, image/jpeg`,
    2: `${localize.audio}/mp3`,
    3: `${localize.video}/mp4`,
    4: `${localize.document}/pdf`,
  };
  return evidenceTypeObj[evidenceTypeId];
};

export const getFileExtension = (filePath) => {
  if (filePath) {
    const splittedPath = filePath.split(filePath.includes("/") ? "/" : "\\");
    const extension = splittedPath[splittedPath.length - 1].split(".");
    return extension[extension.length - 1];
  }

  return "";
};

export const GetDateInYYYYMMDDHHmmssUTC = (date) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  return `${year}-${convertToValueWithZero(month)}-${convertToValueWithZero(
    day,
  )} ${convertToValueWithZero(hours)}:${convertToValueWithZero(
    minutes,
  )}:${convertToValueWithZero(seconds)}`;
};

export const GetDateFormat = () => "DD/MM/YYYY";

export const generateNoDataValues = (quantity) => {
  const noDataList = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < quantity; i++) {
    noDataList.push(<NoDataLabel />);
  }
  return {
    key: 0,
    data: noDataList,
  };
};

export const parseJwt = (token) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(""),
  );

  return JSON.parse(jsonPayload);
};

export const getProfileNameByForm = (formName) => {
  const formNameLabelObj = {
    OPERATIVE_AREA: localize.operational_areas,
    EVENT_TYPE: localize.event_type,
    FORM_FIELD: localize.form_fields,
    DYNAMIC_FORM: localize.dynamic_forms,
    EXECUTION_STATUS: localize.execution_statuses,
    WORKFLOW: localize.workflows,
    ADDITIONAL_NEWNESS: localize.additional_newness,
    ADDITIONAL_COST_TYPE: localize.additional_cost_type_title,
    PAYMENT_TYPE: localize.payment_type_title,
    EVENT: localize.events,
    REPORTED_NEWNESS: localize.reported_newness,
    REGISTER_PAYMENTS: localize.register_payment,
    RELATED_BILLS: localize.related_bills,
    CONTRACTOR: localize.contractors,
    PERSONAL: localize.personal,
    WORK_TEAM: localize.work_teams,
    MEASUREMENT_UNIT: localize.measurement_units,
    SUPPLY: localize.supplies,
    CLIENT: localize.clients,
    RESOLUTION: localize.resolutions,
    VEHICLE_TYPE: localize.vehicle_types,
    VEHICLE: localize.vehicle,
    USER: localize.users,
    PROFILE: localize.profiles,
    POSITION: localize.positions,
    PROCEEDING: localize.proceedings,
    QUOTATION_REQUEST: localize.quotation_requests,
    QUOTATION_SETUP: localize.quotation_setup,
    PRE_ORDER: localize.pre_orders_title,
    DAILY_ROUTE: localize.daily_route_title,
    EVIDENCE_TEMPLATE: localize.evidence_template,
    SUPPLY_REQUEST: localize.supply_requests,
    ADDITIONAL_COST_REQUEST: localize.additional_cost_requests,
    SETTLEMENT: localize.settlements_title,
    EVENT_DETAIL: localize.event_detail_tabs_module,
    INVENTORY_MOVEMENT_TYPE: localize.inventory_movement_type,
    INVENTORY_MOVEMENT: localize.inventory_movement_title,
    WAREHOUSE: localize.warehouse,
    PROVIDER: localize.providers,
    REQUEST_TYPE: localize.request_types,
    REPORT_TYPE: localize.report_types,
    FINISH_CONCEPT: localize.finish_concept,
    PAYMENT_TO_TECHNICIAN_REQUEST: localize.payment_to_technician_request_title,
    GENERAL_REQUESTS: localize.general_requests,
    EVIDENCE_TEMPLATE: localize.evidence_template,
    COMPANY_DATA: localize.company_information,
    DEVICE_CONFIGURATION: localize.device_setup,
  };
  return formNameLabelObj[formName];
};
export const getRestrictionNameByRestrictionKey = (restriction) => {
  const restrictionLabelObj = {
    ACCESS: localize.access,
  };
  return restrictionLabelObj[restriction];
};

export const getActionNameByKey = (actionKey) => {
  const actionLabelObj = {
    ACCESS: localize.access,
    CREATE: localize.action_create,
    MODIFY: localize.action_modify,
    DELETE: localize.action_delete,
    SERVICE_VALUE_CHANGE: localize.action_service_value_change,
    SERVICE_ASSIGNMENT: localize.action_service_assignment,
    BILLING: localize.action_billing,
    SERVICE_PAYMENT_REGISTRATION: localize.action_service_payment_registration,
    PERSONAL_PAYMENT_REGISTRATION:
      localize.action_personal_payment_registration,
    PERSONAL_PAYMENT_APPROVAL: localize.action_personal_payment_approval,
    SUPPLY_APPROVAL: localize.action_supply_approval,
    STATUS_CHANGE: localize.action_status_change,
    STATUS_CHANGE_FORM_MODIFICATION:
      localize.action_status_change_form_modification,
    ADD_EVIDENCES: localize.action_add_evidences,
    DELETE_EVIDENCES: localize.action_delete_evidences,
    DOWNLOAD_EVIDENCES: localize.action_download_evidences,
    ADD_NOTES_TO_EVIDENCES: localize.action_add_notes_to_evidences,
    ADD_ADDITIONAL_COSTS: localize.action_add_additional_costs,
    ADDITIONAL_COSTS_APPROVAL: localize.action_additional_costs_approval,
    REPORT_MODIFICATION: localize.action_report_modification,
    ADD_NOTES: localize.action_add_notes,
    VEHICLE_ASSIGNMENT: localize.action_vehicle_assignment,
    PROFILE_ASSIGNMENT: localize.action_profile_assignment,
    USER_CHANGE: localize.action_user_change,
    PASSWORD_CHANGE: localize.action_password_change,
    SETTLEMENT_STATUS_CHANGE: localize.action_settlement_status_change,
    EVENT_DETAIL_TAB_GENERAL_INFO: localize.general_information,
    EVENT_DETAIL_TAB_ADDITIONAL_DATA: localize.additional_data,
    EVENT_DETAIL_TAB_CLIENT_INFO: localize.client_information,
    EVENT_DETAIL_TAB_RESOLUTIONS: localize.resolutions,
    EVENT_DETAIL_TAB_STATUS_HISTORICAL: localize.status_historical,
    EVENT_DETAIL_TAB_SUPPLIES: localize.supplies_report,
    EVENT_DETAIL_TAB_GENERAL_REQUESTS: localize.general_requests,
    EVENT_DETAIL_TAB_NEWNESSES: localize.newnesses,
    EVENT_DETAIL_TAB_EVIDENCES: localize.evidences,
    EVENT_DETAIL_TAB_ADDITIONAL_COSTS: localize.additional_costs,
    EVENT_DETAIL_TAB_VISIT_REPORT: localize.visit_report,
    EVENT_DETAIL_TAB_BILLING: localize.billing,
    EVENT_DETAIL_TAB_EVENT_PAYMENT: `${localize.payment_of} ${localize.event}`,
    EVENT_DETAIL_TAB_PAYMENT_TO_TECHNICIAN: localize.payment_to_technician,
    EVENT_DETAIL_TAB_TECHNICAL_CONCEPT: localize.technical_concept,
    EVENT_DETAIL_TAB_SETTLEMENT: localize.settlement_detail_title,
  };
  return actionLabelObj[actionKey] ?? actionKey;
};

export const getGoogleMapsLink = (latitude, longitude) =>
  `https://www.google.pt/maps/dir//${latitude},${longitude}/@${latitude},${longitude},733m/data=!3m1!1e3!4m4!4m3!1m0!1m1!4e1?hl=en`;

export const getClassForRotation = (rotation) => "";

export const getValueInCurrency = (currencyName, value, literal = false) =>
  `${currencyName} ${new Intl.NumberFormat().format(
    literal ? value : value.toFixed(0),
  )}
`.replace("\n", "");

export const getEventCode = (eventDetailData) => {
  if (!eventDetailData?.Code) {
    return `${localize.app_name}-${eventDetailData?.Id}`;
  }

  return eventDetailData.Code;
};

export const getQuotationRequestStatusName = (statusCode) => {
  switch (statusCode) {
    case QuotationRequestStatusEnum.Canceled:
      return localize.quotation_status_canceled;
    case QuotationRequestStatusEnum.Generated:
      return localize.quotation_status_generated;
    case QuotationRequestStatusEnum.Accepted:
      return localize.quotation_status_accepted;
    case QuotationRequestStatusEnum.Rejected:
      return localize.quotation_status_rejected;
    default:
      return localize.quotation_status_pending;
  }
};

export const getQuotationRequestsStatusBackColor = (statusCode) => {
  switch (statusCode) {
    case QuotationRequestStatusEnum.Canceled:
      return "white";
    case QuotationRequestStatusEnum.Generated:
      return "lightgray";
    case QuotationRequestStatusEnum.Accepted:
      return "#0CAC1F";
    case QuotationRequestStatusEnum.Rejected:
    default:
      return "#FFFFFF";
  }
};

export const getQuotationRequestStatusForeColor = (statusCode) => {
  switch (statusCode) {
    case QuotationRequestStatusEnum.Canceled:
      return "red";
    case QuotationRequestStatusEnum.Generated:
      return "#000000";
    case QuotationRequestStatusEnum.Accepted:
      return "#FFFFFF";
    case QuotationRequestStatusEnum.Rejected:
      return "#EE2F56";
    case QuotationRequestStatusEnum.Pending:
      return "#10366E";
    default:
      return "#10366E";
  }
};

export const getDatesArray = (initialDate, finalDate) => {
  // start date
  const startDate = new Date(initialDate);

  // end date
  const endDate = new Date(finalDate);

  // array of dates
  const datesArray = [];

  for (
    let date = startDate;
    date <= endDate;
    date.setDate(date.getDate() + 1)
  ) {
    const dateString = GetDateInYYYYMMDDFormatWithSeparator(
      new Date(date),
      "-",
    );
    datesArray.push(dateString);
  }

  return datesArray;
};

export const validateEmailFormat = (emailsArray) => {
  if (!emailsArray || emailsArray.length <= 0) {
    return false;
  }

  let result = true;
  const validRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  for (let index = 0; index < emailsArray.length; index++) {
    if (!emailsArray[index].match(validRegex)) {
      console.log("NOT APPLY");
      result = false;
      break;
    }
  }

  return result;
};

export const isInitialDateGreaterThanFinalDate = (initial, final) => {
  const initialDate = new Date(
    GetDateInYYYYMMDDFormatWithSeparator(initial, "-"),
  ).getTime();
  const finalDate = new Date(
    GetDateInYYYYMMDDFormatWithSeparator(final, "-"),
  ).getTime();
  return initialDate > finalDate;
};

export const dateDifferenceInDays = (date1, date2) => {
  // of two dates
  let differenceInTime = date2.getTime() - date1.getTime();

  // Calculating the no. of days between
  // two dates
  let differenceInDays = Math.round(differenceInTime / (1000 * 3600 * 24));

  return differenceInDays;
};

export const getFiltersInfo = (filters) => {
  if (filters && filters.length > 0) {
    const result = filters
      .map((filter) => `${filter.FriendlyName}: ${filter.Value}`)
      .join(", ");
    return result;
  }

  return "";
};

export const addDaysToDate = (date, days) => {
  let newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const getCurrentDays = (item) =>
  dateDifferenceInDays(new Date(item.ExpectedSolutionDate), new Date());

export const getCurrentDaysForEvent = (item) =>
  dateDifferenceInDays(new Date(item.CreationDate), new Date());

export const getDaysToWarning = (item) =>
  item?.Type && item?.Type?.DaysUntilWarning
    ? item.Type.DaysUntilWarning
    : dateDifferenceInDays(
        new Date(item.CreationDate),
        new Date(item.ExpectedSolutionDate),
      ) / 2;

export const getDaysToError = (item) =>
  item?.Type && item?.Type?.DaysUntilError > 0
    ? item.Type.DaysUntilError
    : dateDifferenceInDays(
        new Date(item.CreationDate),
        new Date(item.ExpectedSolutionDate),
      ) + 1;

export const getStatusText = (status) => {
  switch (status) {
    case GeneralRequestStatusEnum.Pending:
      return localize.request_status_pending;
    case GeneralRequestStatusEnum.InProgress:
      return localize.request_status_in_progress;
    case GeneralRequestStatusEnum.Done:
      return localize.request_status_done;
    case GeneralRequestStatusEnum.NotFinished:
      return localize.request_status_not_finished;
    default:
      return localize.no_data;
  }
};

export const getApprovedRejectedStatusChangesOptions = () => [
  {
    Id: AdditionalStatusCodeEnum.Approved,
    Name: getAdditionalCostStatusName(AdditionalStatusCodeEnum.Approved),
  },
  {
    Id: AdditionalStatusCodeEnum.Rejected,
    Name: getAdditionalCostStatusName(AdditionalStatusCodeEnum.Rejected),
  },
];

export const getBackColor = (status) => {
  switch (status) {
    case GeneralRequestStatusEnum.Pending:
      return "#E0E0E0";
    case GeneralRequestStatusEnum.InProgress:
      return "#3333FF";
    case GeneralRequestStatusEnum.Done:
      return "#00994C";
    case GeneralRequestStatusEnum.NotFinished:
      return "#E61C45";
    default:
      return "#A5ACAA";
  }
};

export const getForeColor = (status) => {
  switch (status) {
    case GeneralRequestStatusEnum.Pending:
      return "#202020";
    case GeneralRequestStatusEnum.InProgress:
      return "#FFFFFF";
    case GeneralRequestStatusEnum.Done:
      return "#FFFFFF";
    case GeneralRequestStatusEnum.NotFinished:
      return "#FFFFFF";
    default:
      return "#202020";
  }
};

export const MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION = 1000;

export const getGeneralStatusName = [
  localize.quotation_status_generated,
  localize.approved,
  localize.rejected,
];

export const getGeneralStatusBackColor = ["#E0E0E0", "#2E7D32", "#E61C45"];
export const getGeneralStatusForeColor = ["#202020", "#FFFFFF", "#FFFFFF"];

export const GeneralStatusEnum = Object.freeze({
  Generated: 0,
  Approved: 1,
  Rejected: 2,
});

export const GeneralRequestStatusEnum = Object.freeze({
  Pending: 0,
  InProgress: 1,
  Done: 2,
  NotFinished: 3,
});

export const QuotationRequestStatusEnum = Object.freeze({
  Canceled: -1,
  Pending: 0,
  Generated: 1,
  Accepted: 2,
  Rejected: 3,
});

export const OperationalStatusEnum = Object.freeze({
  TotalServiceOrders: -1,
  Canceled: 0,
  ToExecute: 1,
  InExecution: 2,
  PendingReview: 3,
  Finished: 4,
  NotFinished: 5,
});

export const EvidenceTypeEnum = Object.freeze({
  Image: 1,
  Audio: 2,
  Video: 3,
  Document: 4,
  ClientSignature: 5,
  PersonalSignature: 6,
  None: -1,
});

export const EvidenceUploadStatusEnum = Object.freeze({
  None: 0,
  Pending: 1,
  Uploading: 2,
  Succeeded: 3,
  Failed: 4,
});
